'use client';

import { useState, useEffect, useRef, ReactNode } from 'react';
import { User, Menu, X } from 'lucide-react';
import { HttpTypes } from '@medusajs/types';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { useTenant } from '@multitenancy/TenantAndVariantContext';
import InlineSearchBar from '../header-classic/inline-search-bar';
import { getCategoryTree } from '@lib/config/category-tree';
import Image from 'next/image';

interface HeaderDopesnowProps {
  regions: HttpTypes.StoreRegion[];
  collections: HttpTypes.StoreCollection[];
  categories: HttpTypes.StoreProductCategory[];
  cartButton: ReactNode;
}

export function HeaderDopesnow({ regions, collections, categories, cartButton }: HeaderDopesnowProps) {
  const storeConfig = useTenant();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const tree = getCategoryTree(storeConfig.sourceStore);

  // Close dropdown when clicking/touching outside the nav (needed for iPad)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return (
    <>
      <header className="sticky top-0 z-50 bg-white">
        {/* Announcement Bar */}
        <div className="bg-black text-white text-center py-2 text-xs font-medium tracking-wide relative">
          <span>
            <span className="font-bold">Free shipping.</span>{' '}
            On all orders over $150.{' '}
            <LocalizedClientLink href="/store" className="underline font-bold">
              Shop now
            </LocalizedClientLink>
          </span>
          <span className="absolute right-4 top-2 text-xs hidden md:inline-flex items-center gap-4">
            <LocalizedClientLink href="/account" className="hover:underline">
              My orders
            </LocalizedClientLink>
            <span className="flex items-center gap-1">🇦🇺 AU</span>
          </span>
        </div>

        {/* Main Nav */}
        <nav className="h-[60px] flex items-center px-4 md:px-10 border-b border-gray-100">
          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="md:hidden p-2 -ml-2 hover:opacity-70"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Logo */}
          <LocalizedClientLink href="/" className="mr-8 flex items-center">
            <Image
              src={`/images/logo_${storeConfig.name}_horizontal.png`}
              alt={storeConfig.displayName}
              width={160}
              height={40}
              className="h-8 w-auto"
            />
          </LocalizedClientLink>

          {/* Nav Links */}
          <div ref={navRef} className="hidden md:flex items-center gap-6 text-sm font-semibold">
            {tree.map((cat) =>
              cat.children && cat.children.length > 0 ? (
                <div
                  key={cat.handle}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(cat.handle)}
                  onMouseLeave={() => setOpenDropdown(null)}
                  onClick={() => setOpenDropdown(openDropdown === cat.handle ? null : cat.handle)}
                >
                  <LocalizedClientLink
                    href={`/categories/${cat.handle}`}
                    className="hover:opacity-70 whitespace-nowrap py-4"
                    onClick={(e) => { e.stopPropagation(); setOpenDropdown(null); }}
                  >
                    {cat.label}
                  </LocalizedClientLink>
                  {openDropdown === cat.handle && (
                    <div className="absolute top-full left-0 bg-white shadow-lg border border-gray-100 min-w-[200px] py-3 z-50">
                      <LocalizedClientLink
                        href={`/categories/${cat.handle}`}
                        className="block px-5 py-2 text-sm hover:bg-[#F5F5F5] font-semibold"
                          onClick={() => setOpenDropdown(null)}
                      >
                        All {cat.label}
                      </LocalizedClientLink>
                      {cat.children.map((child) => (
                        <LocalizedClientLink
                          key={child.handle}
                          href={`/categories/${child.handle}`}
                          className="block px-5 py-2 text-sm font-normal hover:bg-[#F5F5F5]"
                            onClick={() => setOpenDropdown(null)}
                        >
                          {child.label}
                        </LocalizedClientLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <LocalizedClientLink
                  key={cat.handle}
                  href={`/categories/${cat.handle}`}
                  className="hover:opacity-70 whitespace-nowrap"
                >
                  {cat.label}
                </LocalizedClientLink>
              )
            )}
            <LocalizedClientLink
              href="/store"
              className="hover:opacity-70 text-red-600 font-bold"
            >
              SALE
            </LocalizedClientLink>
          </div>

          {/* Right side */}
          <div className="ml-auto flex items-center gap-3">
            {/* Inline Search */}
            <InlineSearchBar />

            {/* Account */}
            <LocalizedClientLink href="/account" className="hover:opacity-70 p-1">
              <User className="w-5 h-5" />
            </LocalizedClientLink>

            {/* Cart */}
            {cartButton}
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-white">
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
            <LocalizedClientLink href="/" onClick={() => setIsMobileMenuOpen(false)}>
              <Image
                src={`/images/logo_${storeConfig.name}_horizontal.png`}
                alt={storeConfig.displayName}
                width={140}
                height={35}
                className="h-7 w-auto"
              />
            </LocalizedClientLink>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 hover:opacity-70"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="px-4 py-6 space-y-1">
            {tree.map((cat) => (
              <div key={cat.handle}>
                <LocalizedClientLink
                  href={`/categories/${cat.handle}`}
                  className="block py-3 text-lg font-semibold border-b border-gray-100 hover:opacity-70"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {cat.label}
                </LocalizedClientLink>
                {cat.children && cat.children.length > 0 && (
                  <div className="pl-4">
                    {cat.children.map((child) => (
                      <LocalizedClientLink
                        key={child.handle}
                        href={`/categories/${child.handle}`}
                        className="block py-2 text-sm text-gray-600 hover:text-black"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {child.label}
                      </LocalizedClientLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <LocalizedClientLink
              href="/store"
              className="block py-3 text-lg font-bold text-red-600 border-b border-gray-100"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              SALE
            </LocalizedClientLink>
          </div>
        </div>
      )}
    </>
  );
}
